import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError, defer, from } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfigService, GridHelperService, EIDCProvider } from '@core';
import { GetAccessTokenUseCase } from '@auth';
import { KendoDataSourceResultDto } from '@shared';
import { CreateInvestigationRequestDto } from '../dtos/create-investigation-request.dto';
import { CreateEmployeeInvestigationRequestDto } from '../dtos/create-employee-investigation-request.dto';
import { CreateInvestigationResponseDto } from '../dtos/create-investigation-response.dto';
import { InvestigationResponseDto } from '../dtos/investigation-response.dto';
import { DashboardInvestigationRowResponseDto } from '../dtos/dashboard-investigation-row-response.dto';
import { InvestigationStatusSnapshotResponseDto } from '../dtos/investigation-status-snapshot-response.dto';
import { InvestigationPrioritySnapshotResponseDto } from '../dtos/investigation-priority-snapshot-response.dto';
import { InvestigationFieldValueResponseDto } from '../dtos/investigation-field-value-response.dto';
import { UpdateInvestigationStatusRequestDto } from '../dtos/update-investigation-status-request.dto';
import { UpdateInvestigationPriorityRequestDto } from '../dtos/update-investigation-priority-request.dto';
import { UpdateInvestigationAssigneesRequestDto } from '../dtos/update-investigation-assignees-request.dto';
import { UpdateInvestigationFieldValuesRequestDto } from '../dtos/update-investigation-field-values-request.dto';
import { InvestigationAttachmentResponseDto } from '../dtos/investigation-attachment-response.dto';
import {
  GetInvestigationAttachmentUploadUrlRequestDto,
  GetInvestigationAttachmentUploadUrlResponseDto,
  ConfirmInvestigationAttachmentUploadRequestDto,
  ConfirmInvestigationAttachmentUploadResponseDto,
  InvestigationAttachmentDownloadUrlResponseDto,
} from '../dtos/investigation-attachment-upload.dto';
import { InvestigationInvolvedEmployeeResponseDto, AddInvolvedEmployeesRequestDto } from '../dtos/investigation-involved-employee-response.dto';
import { InvestigationWatcherResponseDto } from '../dtos/investigation-watcher-response.dto';
import { InvestigationWatcherOptionResponseDto } from '../dtos/investigation-watcher-option-response.dto';
import { InvestigationMessageResponseDto } from '../dtos/investigation-message-response.dto';
import { InvestigationWorkNoteResponseDto } from '../dtos/investigation-work-note-response.dto';
import { InvestigationPrintResponseDto } from '../dtos/investigation-print-response.dto';
import { InvestigationClosingReasonResponseDto } from '../dtos/investigation-closing-reason-response.dto';
import { InvestigationReopeningReasonResponseDto } from '../dtos/investigation-reopening-reason-response.dto';
import { InvestigationConversionReasonResponseDto } from '../dtos/investigation-conversion-reason-response.dto';
import { InvestigationLinkedInvestigationResponseDto } from '../dtos/investigation-linked-investigation-response.dto';
import { InvestigationChecklistStepResponseDto } from '../dtos/investigation-checklist-step-response.dto';
import { InvestigationEmailTemplateSnapshotResponseDto } from '../dtos/investigation-email-template-snapshot-response.dto';
import { InvestigationAssignmentOptionsResponseDto } from '../dtos/investigation-assignment-options-response.dto';
import { InvestigationAssigneeCandidateResponseDto } from '../dtos/investigation-assignee-candidates-response.dto';
import { InvestigationParentChildResponseDto } from '../dtos/investigation-parent-child-response.dto';
import { TransferredCaseFieldValueResponseDto } from '../dtos/transferred-case-field-value-response.dto';
import { ChangeInvestigationTypeRequestDto } from '../dtos/change-investigation-type-request.dto';
import { InvestigationApprovalRequestResponseDto } from '../dtos/investigation-approval-request-response.dto';
import { InvestigationApprovalResponseDto } from '../dtos/investigation-approval-response.dto';
import { GridBaseQuery } from '@core';

@Injectable({ providedIn: 'root' })
export class InvestigationsApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _eidcProvider = inject(EIDCProvider);
  private readonly _getAccessToken = inject(GetAccessTokenUseCase);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/investigations`;

  public createInvestigation(request: CreateInvestigationRequestDto): Observable<CreateInvestigationResponseDto> {
    return this._http.post<CreateInvestigationResponseDto>(this._baseUrl, request);
  }

  /**
   * Creates an investigation via employee portal endpoint.
   * @param request - Employee create investigation request
   * @returns Observable with created investigation response (investigationId and investigationNumber)
   */
  public createEmployeeInvestigation(request: CreateEmployeeInvestigationRequestDto): Observable<CreateInvestigationResponseDto> {
    return this._http.post<CreateInvestigationResponseDto>(`${this._baseUrl}/employee`, request);
  }

  public getInvestigationById(id: string): Observable<InvestigationResponseDto> {
    return this._http.get<InvestigationResponseDto>(`${this._baseUrl}/${id}`);
  }

  public getDashboardInvestigations(params: HttpParams): Observable<KendoDataSourceResultDto<DashboardInvestigationRowResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<DashboardInvestigationRowResponseDto>>(this._baseUrl, { params });
  }

  public getStatusSnapshots(id: string): Observable<InvestigationStatusSnapshotResponseDto[]> {
    return this._http.get<InvestigationStatusSnapshotResponseDto[]>(`${this._baseUrl}/${id}/status-snapshots`);
  }

  public getPrioritySnapshots(id: string): Observable<InvestigationPrioritySnapshotResponseDto[]> {
    return this._http.get<InvestigationPrioritySnapshotResponseDto[]>(`${this._baseUrl}/${id}/priority-snapshots`);
  }

  public getFieldValues(id: string): Observable<InvestigationFieldValueResponseDto[]> {
    return this._http.get<InvestigationFieldValueResponseDto[]>(`${this._baseUrl}/${id}/field-values`);
  }

  public updateStatus(id: string, request: UpdateInvestigationStatusRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/${id}/status`, request);
  }

  public updatePriority(id: string, request: UpdateInvestigationPriorityRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/${id}/priority`, request);
  }

  public updateAssignees(id: string, request: UpdateInvestigationAssigneesRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/${id}/assignees`, request);
  }

  public getInvestigationTypeAssignmentOptions(investigationTypeId: string, search?: string): Observable<InvestigationAssignmentOptionsResponseDto> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    return this._http.get<InvestigationAssignmentOptionsResponseDto>(
      `${this._baseUrl}/investigation-types/${investigationTypeId}/assignment-options`,
      { params }
    );
  }

  public getInvestigationAssigneeCandidates(investigationId: string): Observable<InvestigationAssigneeCandidateResponseDto[]> {
    return this._http.get<InvestigationAssigneeCandidateResponseDto[]>(`${this._baseUrl}/${investigationId}/assignment-options`);
  }

  public getBulkInvestigationAssignmentOptions(investigationIds: string[]): Observable<InvestigationAssigneeCandidateResponseDto[]> {
    return this._http.post<InvestigationAssigneeCandidateResponseDto[]>(`${this._baseUrl}/bulk-assignment-options`, { investigationIds });
  }

  public updateFieldValues(id: string, request: UpdateInvestigationFieldValuesRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/${id}/field-values`, request);
  }

  // Attachments
  public getAttachments(id: string): Observable<InvestigationAttachmentResponseDto[]> {
    return this._http.get<InvestigationAttachmentResponseDto[]>(`${this._baseUrl}/${id}/attachments`);
  }

  public getAttachmentUploadUrl(id: string, request: GetInvestigationAttachmentUploadUrlRequestDto): Observable<GetInvestigationAttachmentUploadUrlResponseDto> {
    return this._http.post<GetInvestigationAttachmentUploadUrlResponseDto>(`${this._baseUrl}/${id}/attachment-upload-url`, request);
  }

  public confirmAttachmentUpload(id: string, request: ConfirmInvestigationAttachmentUploadRequestDto): Observable<ConfirmInvestigationAttachmentUploadResponseDto> {
    return this._http.post<ConfirmInvestigationAttachmentUploadResponseDto>(`${this._baseUrl}/${id}/attachments`, request);
  }

  public getAttachmentDownloadUrl(id: string, attachmentId: string): Observable<InvestigationAttachmentDownloadUrlResponseDto> {
    return this._http.get<InvestigationAttachmentDownloadUrlResponseDto>(`${this._baseUrl}/${id}/attachments/${attachmentId}/download-url`);
  }

  public deleteAttachment(id: string, attachmentId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/attachments/${attachmentId}`);
  }

  // Involved employees
  public getInvolvedEmployees(id: string): Observable<InvestigationInvolvedEmployeeResponseDto[]> {
    return this._http.get<InvestigationInvolvedEmployeeResponseDto[]>(`${this._baseUrl}/${id}/involved-employees`);
  }

  public addInvolvedEmployees(id: string, request: AddInvolvedEmployeesRequestDto): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/involved-employees`, request);
  }

  public removeInvolvedEmployee(id: string, userId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/involved-employees/${userId}`);
  }

  // Watchers
  public getWatchers(id: string): Observable<InvestigationWatcherResponseDto[]> {
    return this._http.get<InvestigationWatcherResponseDto[]>(`${this._baseUrl}/${id}/watchers`);
  }

  public getWatcherOptions(id: string): Observable<InvestigationWatcherOptionResponseDto[]> {
    return this._http.get<InvestigationWatcherOptionResponseDto[]>(`${this._baseUrl}/${id}/watcher-options`);
  }

  public watchInvestigation(id: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/watches`, {});
  }

  public unwatchInvestigation(id: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/watches`);
  }

  public addWatcher(id: string, userId: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/watchers`, { userId });
  }

  public removeWatcher(id: string, userId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/watchers/${userId}`);
  }

  public setWatchInvestigation(investigationId: string, isWatching: boolean): Observable<void> {
    const obs$ = isWatching
      ? this._http.post<void>(`${this._baseUrl}/${investigationId}/watches`, {})
      : this._http.delete<void>(`${this._baseUrl}/${investigationId}/watches`);
    return obs$.pipe(
      catchError((err: { status?: number }) => {
        if (err?.status === 409 || (!isWatching && err?.status === 404)) {
          return of(void 0);
        }
        return throwError(() => err);
      }),
    );
  }

  // Messages
  public getMessages(id: string): Observable<InvestigationMessageResponseDto[]> {
    return this._http.get<InvestigationMessageResponseDto[]>(`${this._baseUrl}/${id}/messages`);
  }

  public addMessage(id: string, messageType: number, body: string): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(`${this._baseUrl}/${id}/messages`, { messageType, body });
  }

  public markMessagesAsRead(id: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/messages/mark-read`, {});
  }

  // Work Notes
  public getWorkNotes(id: string): Observable<InvestigationWorkNoteResponseDto[]> {
    return this._http.get<InvestigationWorkNoteResponseDto[]>(`${this._baseUrl}/${id}/work-notes`);
  }

  public addWorkNote(id: string, body: string): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(`${this._baseUrl}/${id}/work-notes`, { body });
  }

  // Parent/child investigations
  public getParentChildInvestigations(id: string): Observable<InvestigationParentChildResponseDto> {
    return this._http.get<InvestigationParentChildResponseDto>(`${this._baseUrl}/${id}/parent-child`);
  }

  public addChildInvestigations(id: string, childInvestigationIds: string[]): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/children`, { childInvestigationIds });
  }

  public setParentInvestigation(id: string, parentInvestigationId: string): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/parent`, { parentInvestigationId });
  }

  public unlinkParentChildInvestigation(id: string, linkedInvestigationId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/parent-child/${linkedInvestigationId}`);
  }

  public updateInvestigationLinkType(id: string, linkedInvestigationId: string, linkType: 'parent' | 'child' | 'related'): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/linked-investigations/${linkedInvestigationId}/link-type`, { linkType });
  }

  // Linked investigations
  public getLinkedInvestigations(id: string): Observable<InvestigationLinkedInvestigationResponseDto[]> {
    return this._http.get<InvestigationLinkedInvestigationResponseDto[]>(`${this._baseUrl}/${id}/linked-investigations`);
  }

  public linkInvestigation(id: string, linkedInvestigationId: string, linkType = 'related'): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/linked-investigations`, { linkedInvestigationId, linkType });
  }

  public unlinkInvestigation(id: string, linkedInvestigationId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/linked-investigations/${linkedInvestigationId}`);
  }

  // Checklist steps
  public getChecklistSteps(id: string): Observable<InvestigationChecklistStepResponseDto[]> {
    return this._http.get<InvestigationChecklistStepResponseDto[]>(`${this._baseUrl}/${id}/checklist-steps`);
  }

  public updateChecklistStep(id: string, stepId: string, isCompleted: boolean): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/${id}/checklist-steps/${stepId}`, { isCompleted });
  }

  public getEmailTemplateSnapshots(id: string): Observable<InvestigationEmailTemplateSnapshotResponseDto[]> {
    return this._http.get<InvestigationEmailTemplateSnapshotResponseDto[]>(`${this._baseUrl}/${id}/email-template-snapshots`);
  }

  // Close / Reopen
  public getClosingReasons(): Observable<InvestigationClosingReasonResponseDto[]> {
    return this._http.get<InvestigationClosingReasonResponseDto[]>(`${this._baseUrl}/closing-reasons`);
  }

  public getReopeningReasons(): Observable<InvestigationReopeningReasonResponseDto[]> {
    return this._http.get<InvestigationReopeningReasonResponseDto[]>(`${this._baseUrl}/reopening-reasons`);
  }

  public getConversionReasons(): Observable<InvestigationConversionReasonResponseDto[]> {
    return this._http.get<InvestigationConversionReasonResponseDto[]>(`${this._baseUrl}/conversion-reasons`);
  }

  public closeInvestigation(id: string, statusId: string, closingReasonId: string, closingNotes: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/close`, { statusId, closingReasonId, closingNotes });
  }

  public reopenInvestigation(id: string, reopeningReasonId: string, reopeningNotes: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/reopen`, { reopeningReasonId, reopeningNotes });
  }

  public convertToInvestigation(id: string, conversionReasonId: string, conversionNotes: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/convert-to-investigation`, { conversionReasonId, conversionNotes });
  }

  public getInvestigationForPrint(id: string): Observable<InvestigationPrintResponseDto> {
    return this._http.get<InvestigationPrintResponseDto>(`${this._baseUrl}/${id}/print`);
  }

  // Use fetch instead of HttpClient to bypass the EID auditing SDK's XHR patch,
  // which strips responseType and causes Angular to receive text instead of a Blob.
  public exportLegalPacket(id: string, sections: string[]): Observable<Blob> {
    const url = `${this._baseUrl}/${id}/legal-packet`;

    return defer(() => {
      const accessToken = this._eidcProvider.accessToken() || this._getAccessToken.execute()();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'productCode': this._config.productCode,
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      return from(
        fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ sections }),
        }).then((res) => {
          if (!res.ok) throw new Error(`Export failed: ${res.status}`);
          return res.blob();
        }),
      );
    });
  }

  // Transfer-to-ER (HCMI-796): read-only snapshot of the source Case's form values and
  // reclassification endpoint used by the "Convert to ER case type" modal.
  public getTransferredCaseFields(id: string): Observable<TransferredCaseFieldValueResponseDto[]> {
    return this._http.get<TransferredCaseFieldValueResponseDto[]>(`${this._baseUrl}/${id}/transferred-case-fields`);
  }

  public changeInvestigationType(id: string, request: ChangeInvestigationTypeRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/${id}/investigation-type`, request);
  }

  /**
   * Types eligible to reclassify a transferred investigation. Guarded by CaseManagementEditOpen
   * (same permission as the PATCH), so it works for non-admin case handlers. Only returns active
   * non-transfer types with a published intake form — matches the backend validator.
   */
  public getReclassificationOptions(): Observable<ReclassificationOptionResponseDto[]> {
    return this._http.get<ReclassificationOptionResponseDto[]>(`${this._baseUrl}/investigation-types/reclassification-options`);
  }

  // Workflow approvals
  public getApprovalRequests(id: string): Observable<InvestigationApprovalRequestResponseDto[]> {
    return this._http.get<InvestigationApprovalRequestResponseDto[]>(`${this._baseUrl}/${id}/approval-requests`);
  }

  public submitForApproval(id: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/submit-for-approval`, {});
  }

  public sendForClosureApproval(id: string, body: { statusId: string }): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/send-for-closure-approval`, body);
  }

  public routeInvestigation(id: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/route-investigation`, {});
  }

  public approveRequest(id: string, approvalRequestId: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/approval-requests/${approvalRequestId}/approve`, {});
  }

  public denyRequest(id: string, approvalRequestId: string, body: { notes: string }): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/approval-requests/${approvalRequestId}/deny`, body);
  }

  public approveClosureRequest(id: string, approvalRequestId: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/approval-requests/${approvalRequestId}/closure-approve`, {});
  }

  public denyClosureRequest(id: string, approvalRequestId: string, body: { notes: string }): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/approval-requests/${approvalRequestId}/closure-deny`, body);
  }

  // Approvals inbox
  public getApprovalsWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<InvestigationApprovalResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResultDto<InvestigationApprovalResponseDto>>(`${this._baseUrl}/approvals`, { params });
  }
}

export interface ReclassificationOptionResponseDto {
  id: string;
  name: string;
  categoryName: string;
}
